import type { Table } from "@tanstack/react-table";

/**
 * Prints every row a table holds after its filters and sort, not just the ones
 * on screen.
 *
 * The grid only renders the rows near the viewport (it is virtualized), so the
 * browser's own print saw a screenful at most, and always the first one. This
 * builds a plain document instead: the visible columns in their order, one
 * line per row, values as text, the header repeated on every page. It prints
 * from a hidden frame, so the app's layout, scrolling and fixed heights never
 * reach the paper.
 *
 * Values are the column's own value (what it sorts and filters on), formatted
 * plainly: a column that draws a badge prints its word, a progress bar its
 * number. A server-paged table prints the rows it has loaded.
 */
export function printTable<T>(
  table: Table<T>,
  options: { title: string; label: (columnId: string) => string; subtitle?: string },
): void {
  const columns = table.getVisibleLeafColumns().filter((column) => !column.id.startsWith("__"));
  const rows = table.getRowModel().rows;
  const head = columns.map((column) => `<th>${escapeHtml(options.label(column.id))}</th>`).join("");
  const body = rows
    .map((row) => `<tr>${columns.map((column) => `<td>${escapeHtml(text(row.getValue(column.id)))}</td>`).join("")}</tr>`)
    .join("");
  const printedAt = new Date().toLocaleString();
  const html = `<!doctype html><html><head><meta charset="utf-8"><title>${escapeHtml(options.title)}</title>
<style>
  @page { margin: 12mm; }
  body { font: 10px/1.35 system-ui, sans-serif; color: #000; margin: 0; }
  h1 { font-size: 14px; margin: 0 0 2px; }
  p { margin: 0 0 8px; color: #444; }
  table { width: 100%; border-collapse: collapse; }
  thead { display: table-header-group; }
  th, td { border: 1px solid #999; padding: 2px 4px; text-align: left; vertical-align: top; }
  th { background: #eee; font-weight: 600; }
  tr { break-inside: avoid; }
</style></head><body>
<h1>${escapeHtml(options.title)}</h1>
<p>${escapeHtml([options.subtitle, `${rows.length} rows`, printedAt].filter(Boolean).join(" · "))}</p>
<table><thead><tr>${head}</tr></thead><tbody>${body}</tbody></table>
</body></html>`;

  const frame = document.createElement("iframe");
  frame.setAttribute("aria-hidden", "true");
  frame.style.position = "fixed";
  frame.style.width = "0";
  frame.style.height = "0";
  frame.style.border = "0";
  document.body.appendChild(frame);
  const target = frame.contentWindow;
  if (!target) {
    frame.remove();
    return;
  }
  target.document.open();
  target.document.write(html);
  target.document.close();
  // The frame goes once printing is done (or cancelled); a fallback covers
  // browsers that never fire afterprint on a frame.
  const cleanup = () => frame.remove();
  target.addEventListener("afterprint", cleanup, { once: true });
  window.setTimeout(cleanup, 60_000);
  target.focus();
  target.print();
}

function text(value: unknown): string {
  if (value == null) return "";
  if (Array.isArray(value)) return value.map(text).filter(Boolean).join(", ");
  if (value instanceof Date) return value.toLocaleDateString();
  if (typeof value === "number") return Number.isFinite(value) ? value.toLocaleString() : "";
  if (typeof value === "boolean") return value ? "✓" : "";
  if (typeof value === "object") return "";
  return String(value);
}

function escapeHtml(value: string): string {
  return value.replace(/[&<>"']/g, (character) =>
    ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[character] ?? character,
  );
}
